import { roundToDecimalPlaces } from '@semios/app-platform-common'
import { getSoilAWCBackgroundColor } from 'App/Map/_utils/getSoilAWCBackgroundColor'
import { lastDays, nextDays, periodTitle } from 'App/Map/PanelDetails/OverviewCharts/_utils/columnTitles'
import { irrigationActivityColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/irrigationActivityColumns'
import type { TValueGroupColumns } from 'App/Map/PanelDetails/OverviewCharts/_utils/types'
import { valueColumn } from 'App/Map/PanelDetails/OverviewCharts/_utils/valueColumn'
import { EllipsisTooltip } from 'App/Map/PanelDetails/OverviewCharts/EllipsisTooltip'
import type { CurrentValuesDataInfo } from 'App/Map/PanelDetails/OverviewCharts/OverviewColumns'
import { BadgeCell } from 'App/Map/PanelDetails/SummaryGridTable/BadgeCell/BadgeCell'
import { translate } from 'i18n/i18n'
import { isNil } from 'lodash'
import { unitConverter } from 'utils/unitConverter/unitConverter'

const evapotranspirationColumn = (dataIndex: string, label: string, period: string) =>
  valueColumn({
    dataIndex,
    title: periodTitle(label, period),
    width: 170,
    converter: unitConverter.evapotranspiration,
  })

const fruitGrowthColumn = (dataIndex: string, title: string, width: number) =>
  valueColumn({ dataIndex, title, width, converter: unitConverter.fruitGrowth })

const roundAwc = (value: number) =>
  roundToDecimalPlaces(value, unitConverter.soilAwc().defaultNumberOfDecimalPlaces())

export const waterManagementColumns: Record<string, TValueGroupColumns> = {
  evapotranspiration: {
    leadingColumn: 'blockName',
    getColumns: () => {
      const eto = translate.phrases.unitSymbols('ETo')
      const etc = translate.phrases.unitSymbols('ETc')

      return [
        {
          title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Crop')} />,
          dataIndex: 'crop',
          key: 'crop',
          width: 150,
          render: (value: string) => value,
        },
        evapotranspirationColumn('evapotranspirationETo_previous30DaysSum_mm', eto, lastDays(30)),
        evapotranspirationColumn('evapotranspirationETo_previous14DaysSum_mm', eto, lastDays(14)),
        evapotranspirationColumn('evapotranspirationETo_previous7DaysSum_mm', eto, lastDays(7)),
        evapotranspirationColumn('evapotranspirationETo_next7DaysSum_mm', eto, nextDays(7)),
        evapotranspirationColumn('evapotranspirationETo_next14DaysSum_mm', eto, nextDays(14)),
        evapotranspirationColumn('evapotranspirationETc_previous30DaysSum_mm', etc, lastDays(30)),
        evapotranspirationColumn('evapotranspirationETc_previous14DaysSum_mm', etc, lastDays(14)),
        evapotranspirationColumn('evapotranspirationETc_previous7DaysSum_mm', etc, lastDays(7)),
        evapotranspirationColumn('evapotranspirationETc_next7DaysSum_mm', etc, nextDays(7)),
        evapotranspirationColumn('evapotranspirationETc_next14DaysSum_mm', etc, nextDays(14)),
      ]
    },
  },
  fruit_growth: {
    leadingColumn: 'pointName',
    getColumns: () => {
      const fruitGrowth = translate.phrases.banyanApp('Fruit Growth')
      const fruitGrowthTarget = translate.phrases.banyanApp('Fruit Growth Target')

      return [
        fruitGrowthColumn('fruitSize_um', translate.phrases.banyanApp('Fruit Size'), 150),
        fruitGrowthColumn('fruitGrowth_last7DaysSum_um', periodTitle(fruitGrowth, lastDays(7)), 230),
        fruitGrowthColumn('fruitGrowth_last14DaysSum_um', periodTitle(fruitGrowth, lastDays(14)), 230),
        fruitGrowthColumn('fruitGrowth_last30DaysSum_um', periodTitle(fruitGrowth, lastDays(30)), 230),
        fruitGrowthColumn('fruitGrowth_next14DaysSum_um', periodTitle(fruitGrowth, nextDays(14)), 230),
        fruitGrowthColumn(
          'fruitGrowthTarget_next14DaysSum_um',
          periodTitle(fruitGrowthTarget, nextDays(14)),
          280,
        ),
      ]
    },
  },
  irrigation_activity: irrigationActivityColumns,
  plant_stress: {
    leadingColumn: 'pointName',
    getColumns: () => [
      {
        title: (
          <EllipsisTooltip
            fullText={periodTitle(
              translate.phrases.banyanApp('Plant Stress'),
              translate.phrases.banyanApp('Yesterday'),
            )}
          />
        ),
        dataIndex: 'plantStress_yesterday_signalTS5',
        key: 'plantStress_yesterday_signalTS5',
        width: 170,
        render: (value: number) =>
          isNil(value) ? translate.phrases.templates('-') : unitConverter.plantWaterStress(value).value(),
      },
    ],
  },
  soil: {
    leadingColumn: 'pointName',
    getColumns: () => [
      {
        title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Root Zone Average')} />,
        dataIndex: 'root_zone_average',
        key: 'root_zone_average',
        width: 200,
        render: (value: number) => {
          if (isNil(value)) return translate.phrases.templates('-')

          const roundedValue = roundAwc(value)

          return (
            <BadgeCell backgroundColor={getSoilAWCBackgroundColor(roundedValue)}>
              {unitConverter.soilAwc(roundedValue).valueWithSuffix()}
            </BadgeCell>
          )
        },
      },
      ...[1, 2, 3, 4].map((index) => ({
        title: <EllipsisTooltip fullText={translate.phrases.banyanApp('Depth {{index}} AWC', { index })} />,
        dataIndex: `soil_awc_${index}`,
        key: `soil_awc_${index}`,
        width: 150,
        render: (value: number, record: CurrentValuesDataInfo) => {
          if (isNil(value) || !record.fav_depths) return translate.phrases.templates('-')

          const height = parseInt(record.fav_depths[index - 1].replace(/\D/g, ''), 10) * -1
          const awc = unitConverter.soilAwc(roundAwc(value)).valueWithSuffix()

          return `${awc} (-${unitConverter.soilDepth(height).valueWithSuffix()})`
        },
      })),
    ],
  },
}
